import { getEnvVar } from "./utils";
import { readImageBitmapPixels } from "./webgl-pixel-reader";

export const EARTH_RADIUS = 6378137;
export const WORLD_SIZE = 2 * Math.PI * EARTH_RADIUS;
const SOURCE_MAX_ZOOM = 14;
const SOURCE_TILE_PX = 512;
const FETCH_TIMEOUT_MS = 45_000;
const MAPTILER_KEY = getEnvVar("VITE_MAPTILER_API_KEY");

const url = (z: number, x: number, y: number) => {
    return `https://api.maptiler.com/tiles/terrain-rgb-v2/${z}/${x}/${y}.webp?key=${MAPTILER_KEY}`;
};

interface Source {
    width: number;
    height: number;
    heights: Float32Array; // decoded meters, row 0 = north
}

// Numeric key (no per-lookup string alloc): the hot sampling path hits these
// maps thousands of times a frame. Packs (z, x, y) into one safe integer with
// KEY_W-bit coord fields.
const KEY_W = 2 ** 16;
const KEY_Z = 2 ** 32;
const key = (z: number, x: number, y: number) => z * KEY_Z + y * KEY_W + x;

export type DtmRequestRunner = <T>(request: () => Promise<T>) => Promise<T>;

class DtmSources {
    private readonly loading = new Map<number, Promise<Source>>();
    private readonly ready = new Map<number, Source>();
    private revision = 0;

    getRevision(): number {
        return this.revision;
    }

    getReady(k: number): Source | undefined {
        return this.ready.get(k);
    }

    hasReady(k: number): boolean {
        return this.ready.has(k);
    }

    hasLoading(k: number): boolean {
        return this.loading.has(k);
    }

    load(z: number, x: number, y: number, runRequest: DtmRequestRunner = runImmediately): Promise<Source> {
        const k = key(z, x, y);
        const cached = this.loading.get(k);

        if (cached) return cached;

        const pending = this.fetchSource(z, x, y, k, runRequest);
        this.loading.set(k, pending);

        pending.catch(() => {
            if (this.loading.get(k) === pending) {
                this.loading.delete(k);
            }
        });

        return pending;
    }

    async initialize(maxZoom: number): Promise<void> {
        const loads: Promise<unknown>[] = [];
        const maxZ = clampZoom(maxZoom);
        for (let z = 0; z <= maxZ; z++) {
            const n = 2 ** z;
            for (let x = 0; x < n; x++) {
                for (let y = 0; y < n; y++) loads.push(this.load(z, x, y).catch(() => null));
            }
        }
        await Promise.all(loads);
    }

    private async fetchSource(
        z: number,
        x: number,
        y: number,
        k: number,
        runRequest: DtmRequestRunner,
    ): Promise<Source> {
        const blob = await runRequest(async () => {
            const res = await fetch(url(z, x, y), {
                signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
            });

            if (!res.ok) {
                throw new Error(`failed to load ${k}`);
            }

            return res.blob();
        });

        // The RGB channels encode elevation, not colour, so all orientation,
        // premultiplication and colour-space remapping must be disabled.
        const bitmap = await createImageBitmap(blob, {
            imageOrientation: "none",
            premultiplyAlpha: "none",
            colorSpaceConversion: "none",
        });

        // Read dims before close(): closing an ImageBitmap zeroes them.
        const width = bitmap.width;
        const height = bitmap.height;
        const heights = new Float32Array(width * height);
        try {
            const pixels = readImageBitmapPixels(bitmap);

            for (let p = 0; p < heights.length; p++) {
                heights[p] = decodeMeters(pixels, p * 4);
            }
        } finally {
            bitmap.close();
        }

        const source: Source = { width, height, heights };
        this.ready.set(k, source);
        this.revision++;
        return source;
    }
}

const sources = new DtmSources();

export function getElevationRevision(): number {
    return sources.getRevision();
}

const runImmediately: DtmRequestRunner = (request) => request();

// https://docs.mapbox.com/data/tilesets/reference/mapbox-terrain-rgb-v1/#elevation-data
function decodeMeters(data: Uint8Array, idx: number): number {
    return -10000 + 0.1 * (data[idx] * 65536 + data[idx + 1] * 256 + data[idx + 2]);
}

// Bilinear height (m) at a global pos in source-tile units (tx/ty=1 = one tile).
// One continuous pixel grid across tiles, so the same world point always gives
// the same height (watertight edges). Reads neighbours when a tap crosses an edge.
function sampleHeight(tx: number, ty: number, z: number, fallback?: [number, number]): number {
    const n = 2 ** z;
    let cx = Math.min(Math.max(Math.floor(tx), 0), n - 1);
    let cy = Math.min(Math.max(Math.floor(ty), 0), n - 1);
    let base = sources.getReady(key(z, cx, cy));
    if (!base && fallback) {
        // An edge vertex may lie in a neighbour that has not arrived yet.
        [cx, cy] = fallback;
        base = sources.getReady(key(z, cx, cy));
    }
    if (!base) return 0;
    const W = base.width;
    const H = base.height;
    const gw = W * n;
    const gh = H * n;

    const px = tx * W - 0.5; // global pixel coords, pixel k centred at k+0.5
    const py = ty * H - 0.5;
    const x0 = Math.floor(px);
    const y0 = Math.floor(py);
    const fx = px - x0;
    const fy = py - y0;

    const at = (gx: number, gy: number): number => {
        gx = Math.min(Math.max(gx, 0), gw - 1);
        gy = Math.min(Math.max(gy, 0), gh - 1);
        const sx = Math.floor(gx / W);
        const sy = Math.floor(gy / H);
        const src = sources.getReady(key(z, sx, sy));
        if (!src) {
            // A bilinear tap can cross into a neighbour that has not loaded yet.
            // Extend the covering tile's edge instead of indexing outside its array.
            const bx = Math.min(Math.max(gx - cx * W, 0), W - 1);
            const by = Math.min(Math.max(gy - cy * H, 0), H - 1);
            return base.heights[by * W + bx];
        }
        return src.heights[(gy - sy * H) * W + (gx - sx * W)];
    };

    const h00 = at(x0, y0);
    const h10 = at(x0 + 1, y0);
    const h01 = at(x0, y0 + 1);
    const h11 = at(x0 + 1, y0 + 1);
    return (h00 * (1 - fx) + h10 * fx) * (1 - fy) + (h01 * (1 - fx) + h11 * fx) * fy;
}

function mercatorToUv(mercX: number, mercY: number): [number, number] | null {
    const u = mercX / WORLD_SIZE + 0.5; // 0 at west edge, 1 at east edge
    const v = 0.5 - mercY / WORLD_SIZE; // 0 at north edge, 1 at south edge
    if (u < 0 || u >= 1 || v < 0 || v >= 1) return null;
    return [u, v];
}

function sourceTileAt(u: number, v: number, z: number): [number, number] {
    const n = 2 ** z;
    return [Math.floor(u * n), Math.floor(v * n)];
}

function clampZoom(z: number): number {
    return Math.max(0, Math.min(z, SOURCE_MAX_ZOOM));
}

// Scan from the finest source zoom down to root so sampling always uses the
// best loaded tile covering (u,v), even if it's finer than the requested zoom.
// Queueing (to improve later reads) stays bounded to the requested zoom and
// coarser, so we never fetch detail nobody asked for.
function loadedElevationAt(u: number, v: number, startZoom: number, queueMissing: boolean): number | null {
    const start = clampZoom(startZoom);
    for (let z = SOURCE_MAX_ZOOM; z >= 0; z--) {
        const scale = 2 ** z;
        const x = Math.floor(u * scale);
        const y = Math.floor(v * scale);
        const k = key(z, x, y);
        if (sources.hasReady(k)) return sampleHeight(u * scale, v * scale, z);
        // A shared edge belongs to both source tiles. Prefer the canonical
        // covering source, but do not miss finer data loaded on the other side.
        const tx = u * scale;
        const ty = v * scale;
        const left = Number.isInteger(tx) && x > 0;
        const top = Number.isInteger(ty) && y > 0;
        if (left && sources.hasReady(key(z, x - 1, y))) return sampleHeight(tx, ty, z, [x - 1, y]);
        if (top && sources.hasReady(key(z, x, y - 1))) return sampleHeight(tx, ty, z, [x, y - 1]);
        if (left && top && sources.hasReady(key(z, x - 1, y - 1))) {
            return sampleHeight(tx, ty, z, [x - 1, y - 1]);
        }
        if (queueMissing && z <= start && !sources.hasLoading(k)) {
            sources.load(z, x, y).catch(() => null);
        }
    }
    return null;
}

// Warm a coarse global cache for best-effort synchronous height reads.
export function initHeightModel(maxZoom = 2): Promise<void> {
    if (!MAPTILER_KEY) return Promise.resolve();
    return sources.initialize(maxZoom);
}

// Async height fetch at a specific source zoom (clamped); null on miss/failure.
export async function getElevation(mercX: number, mercY: number, tileZoomLevel: number): Promise<number | null> {
    if (!MAPTILER_KEY) return null;
    const uv = mercatorToUv(mercX, mercY);
    if (!uv) return null;

    const z = clampZoom(tileZoomLevel);
    const [u, v] = uv;
    const [x, y] = sourceTileAt(u, v, z);

    try {
        await sources.load(z, x, y);
        const scale = 2 ** z;
        return sampleHeight(u * scale, v * scale, z);
    } catch {
        return null;
    }
}

// Sync best-effort height: sample the finest loaded tile covering the point,
// queue background loads from the requested zoom down to root to improve later reads.
export function getCachedElevation(mercX: number, mercY: number, tileZoomLevel = SOURCE_MAX_ZOOM): number | null {
    if (!MAPTILER_KEY) return null;
    const uv = mercatorToUv(mercX, mercY);
    if (!uv) return null;
    return loadedElevationAt(uv[0], uv[1], tileZoomLevel, true);
}

// Elevation (m) at an EPSG:3857 position, read from the finest loaded source
// tile that covers it; null if nothing covering it is loaded yet.
export function sampleElevation(mercX: number, mercY: number): number | null {
    const uv = mercatorToUv(mercX, mercY);
    if (!uv) return null;
    return loadedElevationAt(uv[0], uv[1], SOURCE_MAX_ZOOM, false);
}

// Sample dyadic global coordinates directly, without a Mercator round trip
// that could move a shared vertex just outside its source-tile boundary.
export function sampleElevationUv(u: number, v: number): number | null {
    if (u < 0 || u > 1 || v < 0 || v > 1) return null;
    return loadedElevationAt(u, v, SOURCE_MAX_ZOOM, false);
}

// Source tiles the grid can touch: the covering tile, plus a neighbour where a
// tile edge lands exactly on a source boundary (the only place a tap crosses).
function neededSources(sourceZoom: number, scale: number, x: number, y: number): [number, number][] {
    const n = 2 ** sourceZoom;
    const sx = Math.floor(x / scale);
    const sy = Math.floor(y / scale);
    const left = x % scale === 0 && sx > 0;
    const right = (x + 1) % scale === 0 && sx + 1 < n;
    const top = y % scale === 0 && sy > 0;
    const bottom = (y + 1) % scale === 0 && sy + 1 < n;
    const xs = [sx, ...(left ? [sx - 1] : []), ...(right ? [sx + 1] : [])];
    const ys = [sy, ...(top ? [sy - 1] : []), ...(bottom ? [sy + 1] : [])];
    const out: [number, number][] = [];
    for (const yy of ys) for (const xx of xs) out.push([xx, yy]);
    return out;
}

function sourceParams(z: number, n: number) {
    // Coarser than the mesh tile: ~1 source px per grid vertex (log2(px/n)),
    // minus one level for 2x safety.
    const bias = Math.floor(Math.log2(SOURCE_TILE_PX / n)) - 1;
    const sourceZoom = Math.max(0, Math.min(z - bias, SOURCE_MAX_ZOOM));
    const scale = 2 ** (z - sourceZoom);
    return { sourceZoom, scale };
}

export function missingDtmSourceKeys(z: number, x: number, y: number, n: number): number[] {
    const { sourceZoom, scale } = sourceParams(z, n);
    const out: number[] = [];
    for (const [sx, sy] of neededSources(sourceZoom, scale, x, y)) {
        const k = key(sourceZoom, sx, sy);
        if (!sources.hasLoading(k)) {
            out.push(k);
        }
    }
    return out;
}

export interface DtmGrid {
    width: number;
    height: number;
    data: Float32Array;
    version: number;
}

export async function sampleDtmGrid(
    z: number,
    x: number,
    y: number,
    n: number,
    runRequest?: DtmRequestRunner,
    onRefine?: (grid: DtmGrid) => void,
): Promise<DtmGrid> {
    const { sourceZoom, scale } = sourceParams(z, n);
    const covering: [number, number] = [Math.floor(x / scale), Math.floor(y / scale)];
    const dim = n + 1;
    const grid: DtmGrid = { data: new Float32Array(dim * dim), width: dim, height: dim, version: 0 };
    if (!MAPTILER_KEY) return grid;

    // Show the covering source immediately; missing neighbours only affect
    // bilinear edge taps and must not hold up the whole tile's elevations.
    await sources.load(sourceZoom, covering[0], covering[1], runRequest);

    const sample = () => {
        for (let j = 0; j <= n; j++) {
            const ty = (y + j / n) / scale;
            for (let i = 0; i <= n; i++) {
                const tx = (x + i / n) / scale;
                grid.data[j * dim + i] = sampleHeight(tx, ty, sourceZoom, covering);
            }
        }
        grid.version++;
    };

    sample();

    for (const [sx, sy] of neededSources(sourceZoom, scale, x, y)) {
        if (sources.hasReady(key(sourceZoom, sx, sy))) continue;
        void sources.load(sourceZoom, sx, sy, runRequest).then(
            () => {
                sample();
                onRefine?.(grid);
            },
            () => {}, // keep the extended edge on a neighbour miss/failure
        );
    }
    return grid;
}
